import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import { connect } from "react-redux";
import MGrid, { ACTION_ADD, MGridColumnDefinition } from "components/Shared/MGrid.tsx";
import {
  DATA_TYPE_STRING,
  DATA_TYPE_BUTTON,
  DATA_TYPE_NUMBER_STRING
} from "typeConstants/dataTypes";
import { CustomerDetailsDto, SurveyTypeListDto, SurveyTypeNotificationDto, NotificationLetterDto, LabelValuePair } from "interfaces/Mueller.Business.Models";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import { userHasRight } from "selectors/rights";
import { CUSTOMER_DETAILS_VIEW, CUSTOMER_NOTIFICATIONS_EDIT } from "typeConstants/rights";
import { getSurveyTypeNotifications } from "requests/customerConfiguration";
import { addNotification, updateNotification, deleteNotification } from "requests/customerConfiguration";
import { getNotificationTypes, getNotificationMethods, getNotificationLetters } from "actions/customerConfiguration";
import AddEditSurveyTypeNotification from "./AddEditSurveyTypeNotification.tsx";

interface CustomerSurveyTypeNotificationsProps {
  verticalDisplay: boolean;
  surveyTypeCodeOverride?: string,  // Optional to override selectedSurveyType prop from redux
  // Redux
  selectedSurveyType: SurveyTypeListDto;
  customerDetails: CustomerDetailsDto;
  canView: boolean;
  canEdit: boolean;
  getNotificationTypes: () => void;
  notificationTypes: LabelValuePair[];
  fetchingNotificationTypes: boolean;
  getNotificationMethods: () => void;
  notificationMethods: LabelValuePair[];
  fetchingNotificationMethods: boolean;
  getNotificationLetters: () => void;
  notificationLetters: NotificationLetterDto[];
  fetchingNotificationLetters: boolean;
}

const CustomerSurveyTypeNotifications = (props: CustomerSurveyTypeNotificationsProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notifications, setNotifications] = useState<SurveyTypeNotificationDto[]>([]);
  const dialog = useContext(DialogContext);
  const originalNotificationTypeCode = useRef("");
  const mounted = useRef(true);

  useEffect(() => {
    if (!props.notificationTypes.length && !props.fetchingNotificationTypes) {
      props.getNotificationTypes();
    }
    if (!props.notificationMethods.length && !props.fetchingNotificationMethods) {
      props.getNotificationMethods();
    }
    if (!props.notificationLetters.length && !props.fetchingNotificationLetters) {
      props.getNotificationLetters();
    }
    return () => { mounted.current = false; }
  }, []);

  useEffect(() => {
    getNotifications();
  }, [props.customerDetails, props.selectedSurveyType, props.surveyTypeCodeOverride]);

  const surveyTypeCode = props.surveyTypeCodeOverride ? props.surveyTypeCodeOverride : props.selectedSurveyType?.code;

  const getNotifications = () => {
    const handleNotifications = (response: SurveyTypeNotificationDto[]) => {
      setNotifications(response);
      setIsLoading(false);
    };
    if (props.canView) {
      getSurveyTypeNotifications(props.customerDetails.number, surveyTypeCode, handleNotifications, mounted);
    }
  };

  // Grid Columns

  const EDIT_COLUMN_KEY = "EDIT_COLUMN_KEY";
  const DELETE_COLUMN_KEY = "DELETE_COLUMN_KEY";

  const memoizedColumns = useMemo(() => {
    if (!props.notificationTypes.length || !props.notificationMethods.length) {
      return [];
    }

    const columns: MGridColumnDefinition[] = [
      {
        key: "notificationTypeCode",
        name: "Notification Type",
        width: 150,
        dataSource: "notificationTypeCode",
        tooltip: (row: SurveyTypeNotificationDto) => props.notificationTypes.find(n => n.value === row.notificationTypeCode)?.label,
        resizable: false,
        dataType: DATA_TYPE_STRING,
      },
      {
        key: "daysFromPrevious",
        name: "Days From Previous",
        width: 150,
        dataSource: "daysFromPrevious",
        resizable: false,
        dataType: DATA_TYPE_NUMBER_STRING,
      },
      {
        key: "previousNotificationCode",
        name: "Previous Notification",
        width: 150,
        dataSource: "previousNotificationCode",
        tooltip: (row: SurveyTypeNotificationDto) => props.notificationTypes.find(n => n.value === row.previousNotificationCode)?.label,
        resizable: false,
        dataType: DATA_TYPE_STRING,
      },
      {
        key: "notificationMethodCode",
        name: "Method",
        width: 150,
        dataSource: "notificationMethodCode",
        tooltip: (row: SurveyTypeNotificationDto) => props.notificationMethods.find(n => n.value === row.notificationMethodCode)?.label,
        resizable: false,
        dataType: DATA_TYPE_STRING,
      },
      {
        key: "letterCode",
        name: "Letter Code",
        width: 150,
        dataSource: "letterCode",
        resizable: false,
        dataType: DATA_TYPE_STRING,
      },
    ];
    const editButton = () => <i className="md-icon material-icons">edit</i>;
    const deleteButton = () => <i className="md-icon material-icons">delete</i>;
    const editColumns = [
      {
        key: EDIT_COLUMN_KEY,
        name: "Edit",
        width: 60,
        dataSource: editButton,
        dataType: DATA_TYPE_BUTTON,
        resizable: false,
        tooltip: "Click to edit notification",
      },
      {
        key: DELETE_COLUMN_KEY,
        name: "Delete",
        width: 60,
        dataSource: deleteButton,
        dataType: DATA_TYPE_BUTTON,
        resizable: false,
        tooltip: "Click to delete notification",
      },
    ];
    if (props.canEdit) {
      columns.unshift(...editColumns);
    }

    return columns;
  }, [props.notificationTypes, props.notificationMethods, notifications]);

  // Grid Buttons + Dialogs

  const handleGridButtons = (selectedOriginalIndex: number, columnKey: string) => {
    if (columnKey == EDIT_COLUMN_KEY && props.canEdit) {
      originalNotificationTypeCode.current = notifications[selectedOriginalIndex].notificationTypeCode;
      dialog.show(
        "Edit Form", 
        <AddEditSurveyTypeNotification 
          notification={notifications[selectedOriginalIndex]} 
          onSave={onSaveEdit} 
          onCancel={dialog.hide} 
          existingNotificationTypes={notifications.reduce((acc, val) => {
            if (val.notificationTypeCode !== notifications[selectedOriginalIndex].notificationTypeCode)
            {
              return [...acc, val.notificationTypeCode];
            }
            return acc;
          }, [])}
        />, 
        null, 
        dialogProps);
    }
    if (columnKey == DELETE_COLUMN_KEY && props.canEdit) {
      const notificationName = props.notificationTypes
        .find(n => n.value === notifications[selectedOriginalIndex].notificationTypeCode)?.label;
      dialog.showYesNo(
        "Confirm Remove Notification",
        `Do you want to remove the notification ${notificationName}? This action cannot be undone.`,
        () => onConfirmDelete(selectedOriginalIndex),
        dialog.hide,
        "Remove",
        "Cancel");
    }
  };

  const dialogProps = { className: "add-notifications-type-form-dialog"};
  const onAdd = () => {
    dialog.show(
      "Add Notification", 
      <AddEditSurveyTypeNotification 
        notification={null} 
        onSave={onSaveNew} 
        onCancel={dialog.hide} 
        existingNotificationTypes={notifications.reduce((acc, val) => {
          return [...acc, val.notificationTypeCode];
        }, [])}
      />, 
      null, 
      dialogProps);
  };

  const onSaveNew = (notification: SurveyTypeNotificationDto) => {
    dialog.hide();
    setIsLoading(true);
    addNotification(props.customerDetails.number, surveyTypeCode, notification.notificationTypeCode, notification, getNotifications, ()=> setIsLoading(false), mounted);
  };

  const onSaveEdit = (notification: SurveyTypeNotificationDto) => {
    dialog.hide();
    setIsLoading(true);
    updateNotification(props.customerDetails.number, surveyTypeCode, originalNotificationTypeCode.current, notification, getNotifications, ()=> setIsLoading(false), mounted);
  };

  const onConfirmDelete = (index: number) => {
    setIsLoading(true);
    deleteNotification(props.customerDetails.number, surveyTypeCode, notifications[index].notificationTypeCode, getNotifications, ()=> setIsLoading(false), mounted);
  };

  const fetchingData = isLoading 
    || !surveyTypeCode 
    || !props.customerDetails 
    || props.fetchingNotificationTypes
    || !props.notificationTypes.length
    || props.fetchingNotificationMethods
    || !props.notificationMethods.length
    || props.fetchingNotificationLetters
    || !props.notificationLetters.length; 

  return (
    <MGrid
      configurationId="CustomerSurveyTypeNotifications"
      headerText="Notifications"
      columnDefinitions={memoizedColumns}
      data={notifications}
      disableFilters
      onCellButtonClick={handleGridButtons}
      isFetchingData={fetchingData}
      displayNumOfItemsFound={true}
      actions={props.canEdit ? [{ code: ACTION_ADD, onClick: onAdd }] : null}
      cardStyle
      minHeight={300}
    />
  );
};

const mapStateToProps = state => ({
  selectedSurveyType: state.customer.selectedSurveyType,
  customerDetails: state.customer.customerDetails,
  canView: userHasRight(CUSTOMER_DETAILS_VIEW)(state),
  canEdit: userHasRight(CUSTOMER_NOTIFICATIONS_EDIT)(state),
  notificationTypes: state.customer.notificationTypes,
  fetchingNotificationTypes: state.customer.fetchingNotificationTypes,
  notificationMethods: state.customer.notificationMethods,
  fetchingNotificationMethods: state.customer.fetchingNotificationMethods,
  notificationLetters: state.customer.notificationLetters,
  fetchingNotificationLetters: state.customer.fetchingNotificationLetters,
});

const mapDispatchToProps = (dispatch) => ({
  getNotificationTypes() {
    dispatch(getNotificationTypes());
  }, 
  getNotificationMethods() {
    dispatch(getNotificationMethods());
  }, 
  getNotificationLetters() {
    dispatch(getNotificationLetters());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerSurveyTypeNotifications);